<script lang="ts">
  import Icon from './Icon.svelte';
  import BookOpen from 'lucide/dist/esm/icons/book-open.mjs';
  import ChevronRight from 'lucide/dist/esm/icons/chevron-right.mjs';
  import Notebook from 'lucide/dist/esm/icons/notebook.mjs';
  import Plus from 'lucide/dist/esm/icons/plus.mjs';
  import Scissors from 'lucide/dist/esm/icons/scissors.mjs';
  import Skull from 'lucide/dist/esm/icons/skull.mjs';
  import SquarePi from 'lucide/dist/esm/icons/square-pi.mjs';
  /**
   * The action bar under a page.
   *
   * One button whose LABEL changes with the Guillotine state, rather than two
   * buttons with one disabled. The distinction matters: the method's rule is
   * about whether dismissal is *available*, and `items_worked_on_page` is the
   * only input to that decision (REPLICATION.md §5.3, A18).
   *
   * * `items_worked_on_page == 0` and dismissal is *available* (not the Final
   *   page, something still active) → "Nothing stands out (Dismiss N items)".
   *   Nothing was worked this visit, so the guillotine is armed.
   * * otherwise on a non-Final page → "Nothing stands out (Next Page)". Working
   *   even one item disarmed it for the rest of the visit.
   * * otherwise on the Final page → "Nothing stands out (First Active Page)" —
   *   the writing page is immune outright, and the sweep continues on the
   *   first ACTIVE page (there is no page past the writing one to go "next" to).
   *
   * `activeCount` is the number still ACTIVE on the page, which is what the
   * dismissal would actually affect.
   */
  interface Props {
    /** A18: incremented when FinishTask/ReenterTask is authored on this page. */
    itemsWorkedOnPage: number;
    /** Tasks still ACTIVE on this page. */
    activeCount: number;
    /** True when this page is the writing (Final) page — dismissal is immune. */
    isFinalPage?: boolean;
    disabled?: boolean;
    onDismiss?: () => void;
    onNext?: () => void;
    /**
     * The close affordance (STEP_7_SPEC.md §3), moved here from the chrome
     * above the stage: `[Close]` records `meta.lastNotebookId` and returns to
     * the list, which refreshes. The store stays mounted — its outbox keeps
     * draining in the background.
     *
     * It renders under the Landing Hub as well, on purpose: the stage stays
     * mounted behind that veil, so the page — close button included — is on
     * screen the whole time instead of popping in once the hub goes. Under the
     * veil it is `inert` and blurred, hence not clickable; leaving the hub is
     * the browser's back button. Optional so a host with no close to offer
     * renders no button.
     */
    onClose?: () => void;
    /**
     * Step 8 (D5): the `[ Browse ]` affordance in the hint row — a visible
     * button, never a hotkey. The callback is optional so the footer stays
     * byte-identical wherever browse is not reachable.
     */
    onBrowse?: () => void;
    /** D5: the `[ Graveyard ]` affordance, same treatment. */
    onGraveyard?: () => void;
    /**
     * R2.2: the debug-log affordance — an ICON-ONLY button in the hint row
     * (same visual family as the other footer buttons), named and titled for
     * discoverability. The callback stays optional so the footer keeps its
     * shape wherever the log is not reachable.
     */
    onDebugLog?: () => void;
    /**
     * R4.6 amended: true while `e` actually does something here — a
     * lastCaptured in THIS notebook. The hint row advertises the shortcut
     * only while it is armed, so `E` never names a dead key.
     */
    editArmed?: boolean;
    /**
     * R4.3: the `[ + Add item ]` affordance — a POINTER path to quick capture.
     * Capture is otherwise keyboard-only (the `C` hotkey), which a touch
     * device cannot reach; this opens the same drawer. Optional, so the footer
     * stays byte-identical wherever capture is not reachable.
     */
    onCapture?: () => void;
  }

  let {
    itemsWorkedOnPage,
    activeCount,
    isFinalPage = false,
    disabled = false,
    onDismiss,
    onNext,
    onClose,
    onBrowse,
    onGraveyard,
    onDebugLog,
    onCapture,
    editArmed = false,
  }: Props = $props();

  const guillotineArmed = $derived(itemsWorkedOnPage === 0);
  const canDismiss = $derived(guillotineArmed && activeCount > 0 && !isFinalPage);

  // The label promises what the button DOES, so it derives off `canDismiss`
  // (the click's actual routing), not off the guillotine state alone: an
  // immune or disarmed button must not read "Dismiss N items". On the Final
  // page the sweep continues on the first ACTIVE page, not a nonexistent
  // "next" page.
  const label = $derived(
    canDismiss
      ? `Nothing stands out (Dismiss ${activeCount} item${activeCount === 1 ? '' : 's'})`
      : isFinalPage
        ? 'Nothing stands out (First Active Page)'
        : 'Nothing stands out (Next Page)',
  );

  function activate() {
    if (canDismiss) onDismiss?.();
    else onNext?.();
  }
</script>

<footer class="page-footer">
  <span class="page-hint" data-testid="page-hint">
    <kbd>J</kbd><kbd>K</kbd> move · <kbd>Space</kbd> focus · <kbd>C</kbd> capture{#if editArmed}
      · <kbd>E</kbd> edit{/if}
  </span>

  {#if onClose}
    <!-- Left end of the button line: `margin-right: auto` eats the row's free
         space, so this parks on the card edge while everything after it stays
         packed right. -->
    <button
      class="action action--close"
      type="button"
      data-testid="notebook-close"
      onclick={onClose}
    >
      <Icon node={Notebook} />
      [ Close ]
    </button>
  {/if}

  {#if onCapture}
    <!-- R4.3: the pointer path to capture — a touch device has no `C` key. -->
    <button class="action" type="button" data-testid="open-capture" onclick={onCapture}>
      <Icon node={Plus} />
      Add item
    </button>
  {/if}

  {#if onBrowse}
    <!-- D5: entry is buttons only — no B/G hotkeys. -->
    <button class="action" type="button" data-testid="open-browse" onclick={onBrowse}>
      <Icon node={BookOpen} />
      Browse
    </button>
  {/if}

  {#if onGraveyard}
    <button class="action" type="button" data-testid="open-graveyard" onclick={onGraveyard}>
      <Icon node={Skull} />
      Graveyard
    </button>
  {/if}

  {#if onDebugLog}
    <!-- R2.2: icon-only — the hint row stays a hint row; the title names it. -->
    <button
      class="action"
      type="button"
      data-testid="open-debuglog"
      title="Debug log"
      aria-label="Debug log"
      onclick={onDebugLog}
    >
      <Icon node={SquarePi} />
    </button>
  {/if}

  <!--
    `aria-disabled` rather than `disabled`: the button must stay focusable so a
    keyboard user can reach it and hear WHY it is unavailable. A disabled
    control is skipped by screen readers entirely, which would hide the
    dismissal rule rather than explain it.
  -->
  <button
    class="action"
    class:action--dismiss={canDismiss}
    type="button"
    data-testid="page-footer-action"
    aria-disabled={disabled || !canDismiss}
    title={canDismiss
      ? 'No item stood out and nothing was worked on this visit.'
      : isFinalPage
        ? 'The writing page is immune from dismissal.'
        : 'You worked an item on this visit, so the guillotine is disarmed.'}
    onclick={activate}
  >
    {#if canDismiss}
      <Icon node={Scissors} />
    {:else}
      <Icon node={ChevronRight} />
    {/if}
    {label}
  </button>
</footer>
